import { ConfigConstants } from '../../../lib/shared/interfaces/environment.interface';

/**
 * Used for handling the translation of media urls between dev and upper environments
 *
 * @param mediaUrl A base URL that is relative to the origin
 * @returns An absolute URL for Preprod when running in DEV
 */
export const prefixBlueMediaLibUrl = (mediaUrl: string): string => {
  try {
    const isDev = !process.env.ENV || process.env.ENV === 'development';
    return isDev ? `https://www.canstarblue.com.au${mediaUrl}` : mediaUrl;
  } catch (error) {
    return `https://www.canstarblue.com.au${mediaUrl}`;
  }
};

export const constants: ConfigConstants = {
  siteName: 'Canstar Blue',
  site: 'blue',
  widgetsUrl: 'https://widgets.canstar.com.au/widgets/',
  apiUrl: 'https://widgets-api.global.ssl.fastly.net/',
  newsletterFormUrl: 'https://canstar.createsend.com/t/i/s/uhuya/',
  surveyUrl: 'https://www.surveymonkey.com/r/QQBGZRH',
  siteUrl: 'https://www.canstarblue.com.au',
  nzSiteUrl: 'https://www.canstarblue.co.nz',
  caasUrl: {
    dev: 'https://cns-angular-content-uat-15.global.ssl.fastly.net/caas',
    uat: 'https://cns-angular-content-uat-15.global.ssl.fastly.net/caas',
    ci: 'https://cns-angular-content-uat-15.global.ssl.fastly.net/caas',
    preprod: 'https://cns-angular-content-prod-2.global.ssl.fastly.net/caas',
    prod: 'https://cns-angular-content-prod-1.global.ssl.fastly.net/caas',
  },
  selectorWidgetPath: 'assets/js/widgets/blue_category_selector/blue_category_selector-prod.js',
  searchWidgetPath: 'assets/js/widgets/blue_search/blue_search-prod.js',
  detailsWidgetPath: 'assets/js/components/facet_details/facet_details-prod.js',
  featuredProductPath: 'assets/js/widgets/featured_product/featured_product-prod.js',
  newsletterStatic: {
    general: 'phhhiu',
    competition: 'mtdtti',
  },
  graphqlServerUrl: 'https://graph.canstar.com.au/graphql',
  digitalApiUrl: 'https://digital.api.canstar.com.au',
  customerSupportPhoneNo: '+61-738-374-100',
  contentUrl: {
    dev: 'https://www.canstarblue.com.au',
    uat: 'https://blueau-15-uat.global.ssl.fastly.net/',
    ci: 'https://blueau-15-uat.global.ssl.fastly.net/',
    preprod: 'https://blueau-2.global.ssl.fastly.net/',
    prod: 'https://www.canstarblue.com.au',
  },
  /**
   * Blue apollo hardcoded disclaimer accordion content.
   * TODO: Delete this after we get dynamic disclaimers going.
   */
  accordion: {
    acf_fc_layout: 'accordion_component',
    title: 'Important Information',
    content: '',
    accordion_tab: [
      {
        tab_title: 'View Disclosures and Important Information',
        tab_content: '',
      },
    ],
  },
  starRatingImg: {
    indicativeStar: prefixBlueMediaLibUrl('/wp-content/uploads/2021/06/indicative-star.svg'),
    filledStar: prefixBlueMediaLibUrl('/wp-content/uploads/2021/08/Blue-star-rating.svg'),
    emptyStar: prefixBlueMediaLibUrl('/wp-content/uploads/2021/08/Empty-star-rating.svg'),
  },
  // For 100% released routes
  apolloRoutes: ['internet', 'gas', 'mobile', 'electricity', 'solar'],
  // For routes we are AB splitting to test in prod
  apolloExperimentRoutes: [],
  // List of the verticals that uses the better product cards design
  verticalsWithBetterProductCards: [],
  //Svg url's to replace font awesome icons
  icons: {
    arrowCircleDown: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/arrow-circle-down.svg'),
    arrowCircleUp: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/arrow-circle-up.svg'),
    arrowDown: prefixBlueMediaLibUrl('/wp-content/uploads/2021/08/long-arrow-down.svg'),
    arrowRight: prefixBlueMediaLibUrl('/wp-content/uploads/2021/11/long-arrow-right.svg'),
    arrowUp: prefixBlueMediaLibUrl('/wp-content/uploads/2021/08/long-arrow-up.svg'),
    arrowV: prefixBlueMediaLibUrl('/wp-content/uploads/2021/08/arrows-v-1.svg'),
    blueLogo: prefixBlueMediaLibUrl('/wp-content/uploads/2024/10/canstar-blue-logo-121x128px.png'),
    canstarRatingIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/06/star.svg'),
    check: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/check.svg'),
    circle: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/circle.svg'),
    clock: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/clock.svg'),
    cross: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/cross.svg'),
    dash: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/dash.svg'),
    dot: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/dot.svg'),
    ellipsis: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/elipses.svg'),
    emptyStar: prefixBlueMediaLibUrl('/wp-content/uploads/2021/06/empty-star.svg'),
    errorIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/error-icon-light.svg'),
    filterIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/10/filter-new.svg'),
    goldLogo: prefixBlueMediaLibUrl('/wp-content/uploads/2017/04/logo.png'),
    headerSearchCross: prefixBlueMediaLibUrl('/wp-content/uploads/2021/11/Vector.svg'),
    headerSearch: prefixBlueMediaLibUrl('/wp-content/uploads/2021/11/Search.svg'),
    indicativeCanstarRatingIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/06/indicative-star.svg'),
    infoCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/info-circle.svg'),
    infoCircleLarge: prefixBlueMediaLibUrl('/wp-content/uploads/2022/07/info-circle-large.svg'),
    menu: prefixBlueMediaLibUrl('/wp-content/uploads/2021/09/menu.svg'),
    na: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/na.svg'),
    padlock: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/padlock.svg'),
    phone: prefixBlueMediaLibUrl('/wp-content/uploads/2021/10/phone.svg'),
    plus: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/plus.svg'),
    rateCity: prefixBlueMediaLibUrl('/wp-content/uploads/rate-city-new.png'),
    search: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/search.svg'),
    shopping: prefixBlueMediaLibUrl('/wp-content/uploads/2021/11/shopping.svg'),
    trash: prefixBlueMediaLibUrl('/wp-content/uploads/2021/08/trash.svg'),
    user: prefixBlueMediaLibUrl('/wp-content/uploads/2021/10/user.svg'),
    simIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2022/02/SimXLarge.svg'),
    award: prefixBlueMediaLibUrl('/wp-content/uploads/2022/02/Frame-4.svg'),
    viewMore: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/view-more.svg'),
    viewLess: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/view-less.svg'),
    filterPanel: prefixBlueMediaLibUrl('/wp-content/uploads/2021/09/filter.svg'),
    arrowDownViewMore: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/view-more.svg'),
    tableCardCtaBtn: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/TableCardCtaBtn.svg'),
    facebookIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/facebook-f.svg'),
    twitterIcon: prefixBlueMediaLibUrl('/wp-content/uploads/x-logo.svg'),
    instagramIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/instagram.svg'),
    youtubeIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/youtube.svg'),
    linkedinIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/linkedIn.svg'),
    glassesIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2022/04/glasses-vector.svg'),
    cardMissing: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/Image-Card-Missing.png'),
    placeholder75x44: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/placeholder-75x44-1-1.png'),
    glassesVector: prefixBlueMediaLibUrl('/wp-content/uploads/2022/04/glasses-vector.svg'),
    emptyStarRating: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/empty-star-rating.svg'),
    blueStarRating: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/blue-star-rating.svg'),
    spinner: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/spinner.svg'),
    googleLogo: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/google-logo.svg'),
    emailIcon: prefixBlueMediaLibUrl('wp-content/uploads/2022/03/email.svg'),
    mapMarker: prefixBlueMediaLibUrl('/wp-content/uploads/2022/07/map-marker.svg'),
    taglineArrow: prefixBlueMediaLibUrl('/wp-content/uploads/2022/07/gold-arrow.svg'),
    upload: prefixBlueMediaLibUrl('/wp-content/uploads/2022/07/upload.svg'),
    selectorDropdownBlue: prefixBlueMediaLibUrl('/wp-content/uploads/2022/06/selector-dropdown-blue.svg'),
    distributedBy: prefixBlueMediaLibUrl('/wp-content/uploads/2022/08/power.svg'),
    powerOff: prefixBlueMediaLibUrl('/wp-content/uploads/2022/08/power-off.svg'),
    trianglWarningIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2022/08/triangle-exclamation.svg'),
    checkCircleFilled: prefixBlueMediaLibUrl('/wp-content/uploads/2022/06/check-circle-filled.svg'),
    lightPaperPlane: prefixBlueMediaLibUrl('/wp-content/uploads/2022/10/paper-plane-light.svg'),
    chevronLeftLight2: prefixBlueMediaLibUrl('/wp-content/uploads/2022/01/chevron-left-light-2.svg'),
    solidSyncAlt: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/sync-alt-regular.svg'),
    solidChevronUp: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/chevron-up-solid.svg'),
    solidCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/circle-solid.svg'),
    solidCaretRight: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/caret-right-solid.svg '),
    solidCaretLeft: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/caret-left-solid.svg'),
    solidCaretDown: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/caret-down-solid.svg'),
    brandGoogle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/fab-google.svg'),
    solidEye: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/eye-solid.svg'),
    solidEyeSlash: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/eye-slash-solid.svg'),
    solidCheck: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/check.svg'),
    solidCross: prefixBlueMediaLibUrl('/wp-content/uploads/2022/01/small-cross.svg'),
    solidMinus: prefixBlueMediaLibUrl('/wp-content/uploads/2022/08/minus-solid.svg'),
    solidAngleRight: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/angle-right-solid.svg'),
    solidChevronLeft: prefixBlueMediaLibUrl('/wp-content/uploads/2022/01/chevron-left-1.svg'),
    exclamationCircleRed: prefixBlueMediaLibUrl('/wp-content/uploads/2024/05/exclamation-circle.svg'),
    solidRedExclamationTriangle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/smallExclamationTriangle.svg'),
    solidRedExclamationCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/02/exclamation-circle.svg'),
    solidCrossCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/times-circle-solid.svg'),
    solidStar: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/star-solid.svg'),
    solidQuestionCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/08/question-circle-solid.svg'),
    solidExternalLinkAltWhite: prefixBlueMediaLibUrl('/wp-content/uploads/2021/12/TableCardCtaBtn.svg'),
    solidExternalLinkAlt: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/external-link-alt-regular.svg'),
    solidMoneyBill: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/money-bill-wave-solid.svg'),
    solidTag: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/tag-solid.svg'),
    solidCertificate: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/certificate-solid.svg'),
    solidFontCase: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/font-case-solid.svg'),
    lightEnvelope: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/envelope.svg'),
    lightUser: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/user.svg'),
    lightLock: prefixBlueMediaLibUrl('/wp-content/uploads/2021/07/padlock.svg'),
    lightCopy: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/copy-light.svg'),
    lightBookmark: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/bookmark-light.svg'),
    lightShareAlt: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/share-alt-light.svg'),
    lightExternalAlt: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/external-link-alt-light.svg'),
    lightQuestionCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/09/question-circle-light.svg'),
    whiteBookMark: prefixBlueMediaLibUrl('/wp-content/uploads/2022/10/bookmark.svg'),
    reload: prefixBlueMediaLibUrl('/wp-content/uploads/2022/12/reload.svg'),
    smallCross: prefixBlueMediaLibUrl('/wp-content/uploads/2022/01/small-cross.svg'),
    chevronDown: prefixBlueMediaLibUrl('/wp-content/uploads/2022/01/chevron-down.svg'),
    emptyStarFill: prefixBlueMediaLibUrl('/wp-content/uploads/2022/05/empty-star.svg'),
    appleLogo: prefixBlueMediaLibUrl('/wp-content/uploads/authapple-1.png'),
    facebookLogo: prefixBlueMediaLibUrl('/wp-content/uploads/authfacebook-1.png'),
    emailLogo: prefixBlueMediaLibUrl('/wp-content/uploads/authemail-1.png'),
    redExclamationCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2022/02/exclamation-circle.svg'),
    reloadBlack: prefixBlueMediaLibUrl('/wp-content/uploads/2023/06/reload-black-1.svg'),
    reset: prefixBlueMediaLibUrl('/wp-content/uploads/2023/06/reset-1.svg'),
    appDownloadIcon: prefixBlueMediaLibUrl('/wp-content/uploads/2023/06/app-download-icon.svg'),
    yellowArrowRight: prefixBlueMediaLibUrl('/wp-content/uploads/long-arrow-right.svg'),
    blackLinkedin: prefixBlueMediaLibUrl('/wp-content/uploads/linkedin.svg'),
    blackTwitter: prefixBlueMediaLibUrl('/wp-content/uploads/x-twitter.svg'),
    blackFacebook: prefixBlueMediaLibUrl('/wp-content/uploads/square-facebook.svg'),
    share: prefixBlueMediaLibUrl('/wp-content/uploads/share.svg'),
    arrowUpRight: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/arrow-up-right.svg'),
    home: prefixBlueMediaLibUrl('/wp-content/uploads/2023/12/home.svg'),
    calendar: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/calendar.svg'),
    facebookFill: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/facebook-f.svg'),
    instagram: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/instagram.svg'),
    twitterX: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/x-twitter.svg'),
    email: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/envelope.svg'),
    newsHolding: prefixBlueMediaLibUrl('/wp-content/uploads/latest-news-blue-holding.png'),
    chevronBlackRight: prefixBlueMediaLibUrl('/wp-content/uploads/chevron-right.svg'),
    chevronBlackLeft: prefixBlueMediaLibUrl('/wp-content/uploads/chevron-left.svg'),
    lightArrowLeft: prefixBlueMediaLibUrl('/wp-content/uploads/light-arrow-left.svg'),
    crossCircle: prefixBlueMediaLibUrl('/wp-content/uploads/cross-circle.svg'),
    arrowRightWhiteCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/arrow-right-blue-circle.svg'),
    carouselArrowLeft: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/blue-carousel-arrow-left.svg'),
    carouselArrowRight: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/blue-carousel-arrow-right.svg'),
    chevronRight: prefixBlueMediaLibUrl('/wp-content/uploads/2022/03/chevron-right.svg'),
    arrowDownBlack: prefixBlueMediaLibUrl('/wp-content/uploads/2021/09/arrow-down.svg'),
    solidOrangeExclamationCircle: prefixBlueMediaLibUrl('/wp-content/uploads/2024/05/exclamation-circle.svg'),
    solidBlueClock: prefixBlueMediaLibUrl('/wp-content/uploads/2024/05/CLOCK-blue.svg'),
    solidFactChecked: prefixBlueMediaLibUrl('/wp-content/uploads/2024/05/Fact-Check-blue.svg'),
    solidGlossary: prefixBlueMediaLibUrl('/wp-content/uploads/2024/05/Glossary-blue.svg'),
    slidersSimple: prefixBlueMediaLibUrl('/wp-content/uploads/2024/05/sliders-simple.svg'),
    chevronRightTealBlue: prefixBlueMediaLibUrl('/wp-content/uploads/chevron-right-teal-blue.svg'),
    snapshot: prefixBlueMediaLibUrl('/wp-content/uploads/2025/06/snapshot-blue.svg'),
    goldAndBlueLogo: prefixBlueMediaLibUrl('/wp-content/uploads/canstar-gold-and-blue-logo.png'),
  },
  latestNewsPlaceholderImage: prefixBlueMediaLibUrl('/wp-content/uploads/2024/02/blue-placeholder.png'),
  appBarImage: prefixBlueMediaLibUrl('/wp-content/uploads/2024/03/app-hand-blue.svg'),
  footerLinks: {
    canstarGold: 'https://www.canstar.com.au',
    canstarBlue: '/',
    rateCity: 'https://www.ratecity.com.au/',
  },
  appURL: 'https://canstarapp.app.link/VxlEXWya3Kb',
};
